import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { api } from "../api";

// 個々のユーザーが設定した「自動ログアウト（無操作タイムアウト）」をクライアント側で実行する。
// - 実操作イベントで無操作タイマーを更新し、閾値を超えたらログアウト（サーバも last_seen で強制）。
// - 失効60秒前に警告を表示。実操作 or「継続」で解除。
// - heartbeat は実操作があった時のみ最大60秒に1回だけ送り、裏のポーリングで延命しないようにする。
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "mousemove", "wheel", "scroll", "touchstart"] as const;
const HEARTBEAT_MS = 60_000;
const WARN_MS = 60_000;

export default function IdleLogout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const minutes = user?.auto_logout_minutes ?? 0;
  const totalMs = minutes > 0 ? minutes * 60_000 : 0;

  const [warnLeft, setWarnLeft] = useState<number | null>(null);
  const lastActivity = useRef(Date.now());
  const lastHeartbeat = useRef(0);
  const loggingOut = useRef(false);

  useEffect(() => {
    if (!user) { setWarnLeft(null); return; }
    loggingOut.current = false;
    lastActivity.current = Date.now();
    lastHeartbeat.current = Date.now();

    const bump = () => { lastActivity.current = Date.now(); };
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, bump, { passive: true });
    const onVisible = () => { if (document.visibilityState === "visible") bump(); };
    document.addEventListener("visibilitychange", onVisible);

    const doLogout = async () => {
      if (loggingOut.current) return;
      loggingOut.current = true;
      try { await logout(); } catch { /* noop */ }
      navigate("/login", { replace: true, state: { autoLoggedOut: true } });
    };

    const tick = () => {
      const now = Date.now();
      const idle = now - lastActivity.current;
      // 自動ログアウトが設定されている時のみ、無操作で失効させる
      if (totalMs > 0) {
        if (idle >= totalMs) { void doLogout(); return; }
        if (idle >= totalMs - WARN_MS) setWarnLeft(Math.max(0, Math.ceil((totalMs - idle) / 1000)));
        else setWarnLeft((p) => (p === null ? p : null));
      }
      // 実操作があった時のみ心拍（有効期限をローリング延長＝ログイン状態を維持）。裏のポーリングでは延命しない。
      if (lastActivity.current > lastHeartbeat.current && now - lastHeartbeat.current >= HEARTBEAT_MS) {
        lastHeartbeat.current = now;
        api.post("/auth/heartbeat").catch(() => {});
      }
    };
    const id = window.setInterval(tick, 1000);

    return () => {
      window.clearInterval(id);
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, bump);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, totalMs]);

  const stay = () => {
    lastActivity.current = Date.now();
    lastHeartbeat.current = Date.now();
    setWarnLeft(null);
    api.post("/auth/heartbeat").catch(() => {});
  };

  if (warnLeft === null) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-800">まもなく自動ログアウトします</h2>
        <p className="mt-2 text-sm text-gray-600">
          無操作の状態が続いています。あと <span className="font-bold text-red-600">{warnLeft}</span> 秒で自動的にログアウトします。
        </p>
        <div className="mt-4 flex justify-end">
          <button className="btn-primary" onClick={stay}>ログインを継続する</button>
        </div>
      </div>
    </div>
  );
}
